export const GALLERY_FILE_NAME_MIN = 3;
export const GALLERY_FILE_NAME_MAX = 50;

export const GALLERY_MAX_UPLOAD_SIZE = 30 * 1024 * 1024;

export const GALLERY_MAX_UPLOAD_SIZE_MESSAGE =
  "The uploading file reaches the maximum limit";

// `beforeUpload` runs once per selected file, so a rejected batch would raise
// one identical toast per file. antd replaces a message that reuses a `key`,
// so these keep each rejection reason down to a single visible toast.
export const GALLERY_UPLOAD_SIZE_ERROR_KEY = "gallery-upload-size-error";
export const GALLERY_UPLOAD_NAME_ERROR_KEY = "gallery-upload-name-error";

export const getUploadBatchSizeError = (fileList = []) => {
  const totalSize = fileList.reduce((sum, file) => sum + (file?.size || 0), 0);
  return totalSize > GALLERY_MAX_UPLOAD_SIZE
    ? GALLERY_MAX_UPLOAD_SIZE_MESSAGE
    : null;
};

export const GALLERY_FILE_NAME_ALLOWED_PATTERN = /^[A-Za-z0-9.\-_(), ]+$/;

export const GALLERY_FILE_NAME_INVALID_CHARS_MESSAGE =
  "File name can only contain letters, numbers, spaces, and - _ . ( ) ,";

export const getGalleryFileNameEditError = (value) => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return null;
  if (trimmed.length > GALLERY_FILE_NAME_MAX) {
    return "File name is too long";
  }
  if (!GALLERY_FILE_NAME_ALLOWED_PATTERN.test(trimmed)) {
    return GALLERY_FILE_NAME_INVALID_CHARS_MESSAGE;
  }
  return null;
};


export const getUploadFileNameError = (fileName) => {
  const trimmed = (fileName ?? "").trim();
  if (trimmed.length > GALLERY_FILE_NAME_MAX) {
    return `File name is too long. Rename it on your device and try again.`;
  }
  return null;
};
export const validateGalleryFileName = (value) => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return "File name cannot be empty";
  if (trimmed.length < GALLERY_FILE_NAME_MIN) {
    return "File name should be at least 3 characters";
  }
  if (trimmed.length > GALLERY_FILE_NAME_MAX) {
    return "File name is too long";
  }
  if (!GALLERY_FILE_NAME_ALLOWED_PATTERN.test(trimmed)) {
    return GALLERY_FILE_NAME_INVALID_CHARS_MESSAGE;
  }
  return null;
};

export const getGalleryFileName = (item) =>
  item?.file_name || item?.path?.split("/").pop() || "";

export const sortGalleryFilesByName = (files) =>
  [...(files || [])].sort((a, b) =>
    getGalleryFileName(a).localeCompare(getGalleryFileName(b), undefined, {
      sensitivity: "base",
      numeric: true,
    })
  );

export const folderHasUploads = (data) => {
  if (!Array.isArray(data) || data.length === 0) return false;

  return data.some((item) => {
    const paths = item?.paths ?? item?.s3_paths ?? [];
    if (Array.isArray(paths)) {
      return paths.some(
        (p) => p && (typeof p === "string" ? p.length > 0 : Object.keys(p).length > 0)
      );
    }
    return !!paths;
  });
};

export const getDefaultUploadFileName = (file) => file?.name || "";

export const GALLERY_FOLDER_KEYS = {
  PATIENT_FILES: "patient_files",
  DISCHARGE_SUMMARY: "discharge_summary",
  CONSENT_FORM: "consent_form",
  RECYCLE_BIN: "recycle_bin",
};

// Uploads store these category folders in the same `folder` field the custom
// labels use (see IpUploadModal), so the All tab can group everything by folder.
export const GALLERY_FOLDER_LABELS = {
  [GALLERY_FOLDER_KEYS.PATIENT_FILES]: "Patient Files",
  [GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY]: "Discharge Summary",
  [GALLERY_FOLDER_KEYS.CONSENT_FORM]: "Consent Form",
  [GALLERY_FOLDER_KEYS.RECYCLE_BIN]: "Recycle Bin",
};

export const GALLERY_UNFILED_LABEL = "Gallery";

export const getGalleryFolderLabel = (folder) => {
  const key = (folder ?? "").trim();
  if (!key) return GALLERY_UNFILED_LABEL;
  return GALLERY_FOLDER_LABELS[key] || key;
};

export const getUploadFolderKey = (item) =>
  item?.folder ?? item?.category_type ?? item?.folder_name ?? "";

/**
 * Order folders for the gallery sections: the custom labels first (in their tab
 * order), then anything unfiled, then the built-in category folders.
 */
export const rankGalleryFolder = (key, folderOrder = []) => {
  const custom = folderOrder.indexOf(key);
  if (custom > -1) return custom;
  if (key === GALLERY_UNFILED_LABEL) return folderOrder.length;
  const builtIn = [
    GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY,
    GALLERY_FOLDER_KEYS.CONSENT_FORM,
    GALLERY_FOLDER_KEYS.PATIENT_FILES,
  ].indexOf(key);
  if (builtIn > -1) return folderOrder.length + 1 + builtIn;
  return folderOrder.length + 100;
};

/**
 * Folder a record belongs to for sectioning purposes. Inside the Recycle Bin
 * the `recycle_bin` category is just the filter that fetched the record, not a
 * folder worth heading a section with, so it counts as unfiled.
 */
const getGallerySectionKey = (item) => {
  const key = getUploadFolderKey(item);
  return key === GALLERY_FOLDER_KEYS.RECYCLE_BIN ? "" : key;
};

const sortFolderSections = (entries, folderOrder, valueKey) =>
  entries
    .map(([key, value]) => ({
      key,
      title: getGalleryFolderLabel(key),
      [valueKey]: value,
    }))
    .sort(
      (a, b) =>
        rankGalleryFolder(a.key, folderOrder) -
          rankGalleryFolder(b.key, folderOrder) ||
        a.title.localeCompare(b.title)
    );

/**
 * Group raw upload records into folder sections for the gallery "All" tab.
 * Recycle bin records are dropped unless `includeRecycleBin` is set, which the
 * Recycle Bin tab uses to section its own contents by their original folder.
 */
export const groupUploadsByFolder = (
  uploads = [],
  folderOrder = [],
  { includeRecycleBin = false } = {}
) => {
  const list = (uploads || []).filter(
    (item) =>
      includeRecycleBin ||
      getUploadFolderKey(item) !== GALLERY_FOLDER_KEYS.RECYCLE_BIN
  );

  // Nothing is actually filed, so render one plain grid rather than a section
  // headed with the catch-all label.
  if (!list.some((item) => getGallerySectionKey(item))) {
    return list.length === 0
      ? []
      : [{ key: "__all__", title: null, data: list }];
  }

  const buckets = new Map();
  list.forEach((item) => {
    const key = getGallerySectionKey(item) || GALLERY_UNFILED_LABEL;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(item);
  });

  return sortFolderSections([...buckets.entries()], folderOrder, "data");
};

/**
 * Same folder sections as `groupUploadsByFolder`, but for a list of files that
 * has already been flattened out of its upload records.
 */
export const buildGalleryFolderSections = (files = [], folderOrder = []) => {
  const list = files || [];

  if (!list.some((file) => getGallerySectionKey(file))) {
    return [{ key: "__all__", title: null, files: list }];
  }

  const buckets = new Map();
  list.forEach((file) => {
    const key = getGallerySectionKey(file) || GALLERY_UNFILED_LABEL;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(file);
  });

  return sortFolderSections([...buckets.entries()], folderOrder, "files");
};

// Uploads are returned per upload record; merge lists from several requests
// without showing the same record twice.
export const dedupeUploads = (lists = []) => {
  const seen = new Set();
  const merged = [];

  lists.flat().forEach((item) => {
    if (!item) return;
    const id =
      item?.upload_id ??
      `${getUploadFolderKey(item)}|${item?.uploaded_at}|${JSON.stringify(
        item?.paths ?? item?.s3_paths ?? ""
      )}`;
    if (seen.has(id)) return;
    seen.add(id);
    merged.push(item);
  });

  return merged;
};

export const GALLERY_PATIENT_APP_FOLDERS = [
  GALLERY_FOLDER_KEYS.PATIENT_FILES,
  GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY,
  GALLERY_FOLDER_KEYS.CONSENT_FORM,
];

export const isGalleryPatientAppFolder = (activeKey) =>
  GALLERY_PATIENT_APP_FOLDERS.includes(activeKey);

export const PATIENT_APP_UPLOAD_NOTE =
  "Files uploaded here will be visible to the patient in the Patient App.";
